import { css, cx } from "@emotion/css";
import { EnvironmentIcon, MessageLinesIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, borderWidth, colorScales, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
import React from "react";
import { resetStyles } from "../../utils/resetStyles";
import { Tooltip } from "../Tooltip";
import type { ProcessOverviewStepActionData, StepEnvironmentConfig } from "./types";
export interface ProcessOverviewStepActionProps {
    stepActionData: ProcessOverviewStepActionData;
    showNote: boolean;
}
export function ProcessOverviewStepAction({ stepActionData, showNote }: ProcessOverviewStepActionProps) {
    const { id, name, icon, iconTooltip, href, note, isDisabled, summary, environmentConfig } = stepActionData;
    return (<a className={cx(styles.root, { [styles.disabled]: isDisabled })} href={href}>
            <Tooltip content={iconTooltip}>{"src" in icon ? <img className={cx(styles.icon, { [styles.disabled]: isDisabled })} src={icon.src} alt=""/> : icon}</Tooltip>
            <div className={styles.stepContent}>
                <div className={styles.stepContentTop}>
                    <div className={styles.title}>
                        {name}
                        {!showNote && note && <MessageLinesIcon size={20} color={themeTokens.color.icon.tertiary} ariaLabel={"Step note available"}/>}
                    </div>
                    {summary}
                    <p className={styles.environmentConfig}>
                        <EnvironmentIcon size={16} color={themeTokens.color.icon.secondary}/>
                        <span>{getEnvironmentDescription(environmentConfig)}</span>
                    </p>
                </div>
                {showNote && note && <div className={styles.stepNote}>{note}</div>}
            </div>
        </a>);
}
function getEnvironmentDescription(environmentConfig: StepEnvironmentConfig) {
    switch (environmentConfig.type) {
        case "all":
            return "Runs for any environment";
        case "selected":
            return `Runs for: ${environmentConfig.environmentNames.join(", ")}`;
        case "skipped":
            return `Skips: ${environmentConfig.environmentNames.join(", ")}`;
        default:
            exhaustiveCheck(environmentConfig, "Not all types of step environment config have been handled");
    }
}
const styles = {
    root: css({
        ...resetStyles.anchor,
        display: "flex",
        flex: 1,
        padding: `${space[12]}`,
        gap: space[12],
        border: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
        backgroundColor: themeTokens.color.background.primary.default,
        borderRadius: borderRadius.medium,
        font: text.body.regular.small,
        ":focus-visible": {
            boxShadow: themeTokens.shadow.focused,
        },
        ":hover": {
            borderColor: colorScales.blue[300],
            backgroundColor: colorScales.blue[100],
        },
    }),
    stepContent: css({
        display: "flex",
        flex: 1,
        flexDirection: "column",
        gap: space[8],
    }),
    stepContentTop: css({
        display: "flex",
        gap: space[2],
        flexDirection: "column",
    }),
    icon: css({
        display: "block",
        background: themeTokens.color.background.primary.default,
        borderRadius: borderRadius.extraSmall,
        height: "2.5rem",
        width: "2.5rem",
        objectFit: "scale-down",
    }),
    title: css({
        display: "flex",
        gap: space[4],
        font: text.body.bold.medium,
    }),
    environmentConfig: css({
        ...resetStyles.paragraph,
        display: "flex",
        gap: space[4],
        alignItems: "center",
        color: themeTokens.color.text.secondary,
    }),
    stepNote: css({
        padding: `${space[12]} ${space[16]}`,
        backgroundColor: themeTokens.color.callout.background.info.default,
        borderRadius: borderRadius.small,
    }),
    disabled: css({
        opacity: 0.5,
    }),
};
